<template>
  <div>
    <div class="layout-padding">

      <div class="row gutter-sm q-mb-md">
        <div class="col">
          <q-card class="dash-number-box bg-white">
            <q-card-title class="text-center">
              {{ $t('beta_headline') }}
            </q-card-title>

            <q-card-separator />

            <q-card-main>
              {{ $t('beta_content') }}
            </q-card-main>
            <q-card-footer class="row justify-end">
              <q-btn flat color="primary" @click="handleNewVersion">
                {{ $t('beta_button') }}
              </q-btn>
            </q-card-footer>
          </q-card>
        </div>
      </div>

      <div class="row gutter-sm" v-if="statisticsLoaded">
        <div class="col-md-4 col-12">
          <revenue-card
          :revenue="revenue.value"
          :currency="currency"
          :average="revenueAverage"
          @averageYearChanged="handleRevenueAverageYearChanged"
        />
        </div>

        <div class="col-md-4 col-12">
          <overdue-invoices-card
            :amount="overdueInvoiceAmount.value"
            :number="overdueInvoiceNumber.value"
            :currency="currency"
            :style="{ height: 'calc(50% - 0.5rem)', marginBottom: '1rem' }"
          />

          <unpaid-invoices-card
            :amount="unpaidInvoiceAmount.value"
            :number="unpaidInvoiceNumber.value"
            :currency="currency"
            :style="{ height: 'calc(50% - 0.5rem)' }"
          />
        </div>

        <div class="col-md-4 col-12">
          <expenses-card
            :expenses="expensesStatistic.value"
            :currency="currency"
            :average="expensesAverage"
            @averageYearChanged="handleExpensesAverageYearChanged"
          />
        </div>
      </div>

      <div class="row gutter-sm q-mt-none" v-if="statisticsLoaded">
        <div class="col">
          <q-card class="dash-number-box bg-white">
            <q-card-title class="text-center">
              {{ $t('last_invoices') }}
            </q-card-title>

            <q-card-separator />

            <q-card-main>
              <invoice-table v-if="lastInvoices.length" :invoices="lastInvoices" class="no-shadow" />

              <template v-else>
                <div class="row justify-center items-center col-grow q-pt-lg">
                  <div class="text-center">
                    <img src="~assets/emptystates/invoices_empty.svg" width="30%" />

                    <h6 class="q-my-md text-faded">{{ $t('invoices_empty') }}</h6>

                    <q-btn @click="handleCreateInvoice"  color="primary" class="q-mt-lg" outline>
                      {{ $t('create_invoice') }}
                    </q-btn>
                  </div>
                </div>
              </template>
            </q-card-main>
          </q-card>
        </div>
      </div>

      <div class="row gutter-sm q-mt-none" v-if="statisticsLoaded">
        <div class="col">
          <q-card class="dash-number-box bg-white">
            <q-card-title class="text-center">
              {{ $t('last_activities') }}
            </q-card-title>

            <q-card-separator />

            <q-card-main>
              <activities-list :activities="activities"/>
            </q-card-main>
          </q-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { dashboard, general } from 'src/config'
  import { mapActions, mapGetters } from 'vuex'
  import ActivitiesList from 'components/Activities/ActivitiesList.vue'
  import ExpensesCard from 'components/Dashboard/ExpensesCard.vue'
  import InvoiceTable from 'components/Invoices/Table/StaticInvoiceTable.vue'
  import OverdueInvoicesCard from 'components/Dashboard/OverdueInvoicesCard.vue'
  import RevenueCard from 'components/Dashboard/RevenueCard.vue'
  import UnpaidInvoicesCard from 'components/Dashboard/UnpaidInvoicesCard.vue'
  import store from 'src/store'

  export default {
    computed: {
      ...mapGetters([
        'activities',
        'activitiesLoaded',
        'statisticsLoaded',
        'overdueInvoiceAmount',
        'overdueInvoiceNumber',
        'unpaidInvoiceAmount',
        'unpaidInvoiceNumber',
        'newCustomers',
        'revenue',
        'expensesStatistic',
        'invoices',
        'revenueAverage',
        'expensesAverage'
      ]),

      lastInvoices () {
        // Only show as many invoices as defined in the config
        return this.invoices.slice(0, dashboard.num_invoice_entries)
      },

      numberOfInvoices () {
        return dashboard.num_invoice_entries
      },

      currency () {
        return general.base_currency
      }
    },

    methods: {
      ...mapActions(['fetchStatisticsOverview', 'listActivities', 'setRevenueAverageYear', 'setExpensesAverageYear']),

      handleCreateInvoice () {
        this.$router.push({ name: 'create-invoice' })
      },

      handleRevenueAverageYearChanged (value) {
        this.setRevenueAverageYear(value)
        this.fetchStatisticsOverview()
      },

      handleExpensesAverageYearChanged (value) {
        this.setExpensesAverageYear(value)
        this.fetchStatisticsOverview()
      },

      handleNewVersion () {
        window.location.href = general.beta_link
      }
    },

    beforeRouteEnter (to, from, next) {
      const pagination = {
        page: 1,
        rowsNumber: 0,
        rowsPerPage: 15,
        descending: true,
        sortBy: 'created_at',
        filterBy: [['status', '!=', 'storno']]
      }

      let activities = store.dispatch('listActivities')
      let statistics = store.dispatch('fetchStatisticsOverview')
      let invoicesOverview = store.dispatch('listInvoices', { pagination })

      Promise.all([activities, statistics, invoicesOverview]).then(next)
    },

    beforeRouteUpdate (to, from, next) {
      const pagination = {
        page: 1,
        rowsNumber: 0,
        rowsPerPage: 15,
        descending: true,
        sortBy: 'created_at',
        filterBy: [['status', '!=', 'storno']]
      }

      let activities = store.dispatch('listActivities')
      let statistics = store.dispatch('fetchStatisticsOverview')
      let invoicesOverview = store.dispatch('listInvoices', { pagination })

      Promise.all([activities, statistics, invoicesOverview]).then(next)
    },

    components: {
      ActivitiesList,
      InvoiceTable,
      OverdueInvoicesCard,
      UnpaidInvoicesCard,
      RevenueCard,
      ExpensesCard
    }
  }
</script>

<style src="components/Dashboard/dash-number-box.styl" lang="stylus"></style>
